/**
 * Abstract representation of map services, which are root level collections of map layers. Provides functionality that supports interaction with a map service and its underlying layers.
 *
 * Provides backbone event functionality to inheritors in addition to the following fields, functions, and events.
 * ## Fields
 * Name | Type | Description
 * --- | --- | ---
 * name | `string` | The name of this service. Alias to `getName`.
 * layers | `Array` | Gets the layers (of type `MapLayerBase`) at the root of this service. Alias to `getLayers`.
 * visible | `Boolean` | True if this service is marked as visible, otherwise false. Alias to `isVisible` and `setVisible`.
 *
 * ## Events
 * Name | Description | Arguments
 * --- | --- | ---
 * visibility-changed | Fired whenever the visibility of this service is changed. | A reference to this object and the current value of `visible`.
 *
 * @module app/models/map/base/MapServiceBase
 */
import _ from "underscore";
import Backbone from "backbone";
import $ from "jquery";
import GeoUtils from "app/utils/GeoUtils";
import extend from "backbone-extend-standalone";
import Field from "core/models/Field";
import session from "app/Session";
import LayerContainerMixin from "./LayerContainer.mixin";
/**
 * Constructor.
 * @param {object} options The options map.
 * @param {string} [options.name] The display name of this service.
 * @param {Boolean} [options.visible] The initial value of the `visible` field.
 * @param {app/Context} options.context The context associated with this layer.
 */
function MapServiceBase(options) {
  _.extend(this, Backbone.Events);

  var opts = _.defaults({}, options, {
    visible: true,
    selectable: true,
    name: "",
  });
  Object.defineProperty(this, "name", {
    get: _.bind(this.getName, this),
  });
  Object.defineProperty(this, "layers", {
    get: _.bind(this.getLayers, this),
  });
  Object.defineProperty(this, "visible", {
    get: _.bind(this.isVisible, this),
    set: _.bind(this.setVisible, this),
  });
  Object.defineProperty(this, "maxRecordCount", {
    get: _.bind(this.getMaxRecordCount, this),
  });

  this._name = opts.name;
  this._visible = opts.visible;
  this._selectable = opts.selectable;

  this.initialize(options);
}

MapServiceBase.prototype = _.extend(
  {
    /**
     * Inheritors may use this function to execute additional instructions during object construction.
     * @param  {object} options The options map sent to the constructor.
     */
    initialize: function (options) {},

    /**
     * Loads the underlying data.
     * @async
     * @return {Promise} A promise object which completes when loading completes and data is ready. The promise shall resolve with a reference to `this` object and the `layers` property of this object.
     */
    load: function () {
      var deferred = new $.Deferred();

      deferred.resolve(this, this.layers);

      return deferred.promise();
    },

    /**
     * Gets the name of this service.
     * @return {string} The service name.
     */
    getName: function () {
      return this._name;
    },

    /**
     * Gets the layers at the root of this service.
     * @return {Array} The array of `MapLayerBase` objects at the root of this service.
     */
    getLayers: function () {
      return [];
    },

    /**
     * Generates a list of objects containing "value" and "text" keys corresponding to the layers in the given service.
     * @param {Boolean} [onlyIncludeWritable] True to only include the names of layers which can be added to.
     * @return {Array}         The list of resulting objects.
     */
    getLayerNames: function (onlyIncludeWritable) {
      var layerNames = [];

      function createNames(layer, parentLayer, prefix) {
        var childPrefix;
        var children = layer.children;
        var skip = false;
        var name;

        if (!parentLayer) {
          //if we are at a root layer
          childPrefix = "-";
        } else {
          childPrefix = prefix + "-";
        }

        name = prefix + layer.name;

        if (
          !(layer.featureLayer && layer.featureLayer.allows(session, "add"))
        ) {
          skip = !!onlyIncludeWritable;
        }

        if (!skip) {
          layerNames.push({
            value: String((layer.featureLayer || layer.info).id),
            text: name,
          });

          _.each(children, function (childLayer) {
            if (childLayer) {
              createNames(childLayer, layer, childPrefix);
            }
          });
        }
      }

      _.each(this.layers, function (layer) {
        createNames(layer, null, "");
      });

      return layerNames;
    },

    /**
     * Creates the field to be returned by `getLayerField`.
     * @protected
     * @param {Boolean} [onlyIncludeWritableLayers] True to only include the names of layers which can be added to.
     * @return {core/models/Field} The field to be used to retrieve and manipulate the layer ID of a given model of a layer in the service.
     */
    createLayerField: function (onlyIncludeWritableLayers) {
      return null;
    },

    /**
     * Creates the field to be returned by `getServiceField`.
     * @protected
     * @return {core/models/Field} The field to be used to retrieve the service name of a given model in the service.
     */
    createServiceField: function () {
      var self = this;

      return new Field(
        {
          id: "service",
          name: "service",
          label: "Service",
          editor: "text",
          editable: false,
          filterable: true,
          sortable: true,
          viewOptions: {
            tagName: "p",
            className: "form-control-static",
          },
        },
        {
          getter: function () {
            return self.name;
          },
        }
      );
    },

    /**
     * Gets a field which can be used to retrieve and manipulate the layer ID of a given model of a layer in the service.
     * @param {Boolean} [onlyIncludeWritableLayers] True to only include the names of layers which can be added to.
     * @return {core/models/Field} The field to use.
     */
    getLayerField: function (onlyIncludeWritableLayers) {
      return this.createLayerField(onlyIncludeWritableLayers);
    },

    /**
     * Gets a field which can be used to retrieve the service name of this service.
     * @return {core/models/Field} The field to use.
     */
    getServiceField: function () {
      return this.createServiceField();
    },

    /**
     * Gets the maximum number of records returned in a single query, or 0, if there is no maximum.
     * @return {Integer} The maximum number of query results.
     */
    getMaxRecordCount: function () {
      return 0;
    },

    /**
     * Gets the extent of all layers within this service.
     * @return {esri/geometry/Extent} The extent.
     */
    getExtent: function () {
      return GeoUtils.unionExtents(this.layers);
    },

    /**
     * Gets a value indicating whether or not this service is marked as visible.
     * @return {Boolean} True if visible, otherwise false.
     */
    isVisible: function () {
      return this._visible;
    },

    /**
     * Sets the visibility of this service. Note that this setting does *not* propagate to child layers.
     * @param {Boolean} visible True if visible, otherwise false.
     */
    setVisible: function (visible) {
      this._visible = visible;
      this.trigger("visibility-changed", this, visible);
    },

    /**
     * Gets a value indicating whether or not layers in this service should support interaction.
     * @return {Boolean} True to support interaction, otherwise false.
     */
    isSelectable: function () {
      return this._selectable;
    },

    /**
     * Sets a value indicating whether or not layers in this service should support interaction.
     * @param {Boolean} value True to support interaction, otherwise false.
     */
    setSelectable: function (value) {
      this._selectable = value;
      this.trigger("selectability-changed", value);
    },

    /**
     * Marks the service as invisible.
     */
    hide: function () {
      if (this.isVisible()) {
        this.setVisible(false);
      }
    },

    /**
     * Marks the service as visible.
     */
    show: function () {
      if (!this.isVisible()) {
        this.setVisible(true);
      }
    },

    /**
     * Generate a summary of the service.
     * @return {Object} The summary object.
     */
    summarize: function () {
      return {
        summary_layers: {},
        summary_fields: [],
      };
    },

    /**
     * Gets the model which provides the data for this model.
     * @return {MapService} The model instance.
     */
    getWrappedModel: function () {
      return this.service;
    },

    /**
     * Gets a value indicating whether or not this service supports exporting of data.
     * @returns {Boolean} True if export is supported, otherwise false.
     */
    canExport: function () {
      return this.getWrappedModel() && this.getWrappedModel().canExport();
    },
  },
  LayerContainerMixin
);

/**
 * Backbone-style extend function.
 * @function
 * @name extend
 * @see util/extend
 * @scope instance
 * @memberOf module:app/models/map/base/MapServiceBase
 */
MapServiceBase.extend = extend;

export default MapServiceBase;



//////////////////
// WEBPACK FOOTER
// ./public/js/app/models/map/base/MapServiceBase.js
// module id = 354
// module chunks = 1